import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import StepProgress from '../components/StepProgress';
import SelectionCard from '../components/SelectionCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ChevronLeft, ChevronRight, Plus, Trash2, Info, AlertTriangle, ExternalLink, HelpCircle, List, LayoutGrid, Copy, Upload, Download } from 'lucide-react';
import { findBestPvSystemSolutions, calculateColdVoc, calculateEstimatedVmp, calculateStringPower, getOrientationFactor } from '../lib/pv-sizing';
import { PV_DEFAULTS } from '../config/pv-product-config';
import { getDisplayName, BATTERY_MODULE_BRUTTO_KWH } from '../config/general-product-config';
import { exportGeneralPdf, exportPvPdf } from '../lib/pdf-export';
import { orientationOptions } from '../config/defaults';
import axios from 'axios';
import FragHansFloating from './FragHansFloating';
import { API_BASE } from '../lib/api-base';

const API = `${API_BASE}/api`;
const STEPS = [{ label: 'Eingabemodus' }, { label: 'Daten' }, { label: 'Ergebnis' }];

const HANS_PV_GREETINGS = [
  'Du wählst gerade den Eingabemodus. "Strings bekannt" passt, wenn du bereits fertige Strings hast; "Modulfelder" wenn ich die Strings für dich auslegen soll. Unsicher? Beschreib dein Dach — ich leite dich durch.',
  'Du erfasst deine Module. Nenn mir einfach Modulanzahl, Ausrichtung und ggf. den Modultyp (z.B. "Sunpro N96R12 450W") — Voc, Vmp und Temperaturkoeffizient übernehme ich automatisch.',
  'Hier ist dein Ergebnis. Soll ich dir die Stringaufteilung erklären oder Alternativen prüfen?',
];

const emptyString = () => ({ id: Date.now()+Math.random(), stringName:'', orientation:'', moduleType:'', moduleCount:'', vocPerModule:'', powerWpPerModule:'', nominalCurrentPerModule:'', betaVocPercent:'', minDesignTempC:'', iscPerModule:'', vmpPerModule:'' });
const emptyField = () => ({ id: Date.now()+Math.random(), fieldName:'', orientation:'', moduleType:'', moduleCount:'', vocPerModule:'', powerWpPerModule:'', nominalCurrentPerModule:'', betaVocPercent:'', minDesignTempC:'', iscPerModule:'', vmpPerModule:'' });

export default function PvConfigurator() {
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState(null);
  const [strings, setStrings] = useState([emptyString()]);
  const [fields, setFields] = useState([emptyField()]);
  const [result, setResult] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [moduleVariants, setModuleVariants] = useState(null);
  const [annualConsumption, setAnnualConsumption] = useState(''); // {idx, variants}
  const [recommendedModelOverride, setRecommendedModelOverride] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Restore state when returning from LeadForm or prefill from Frag HANS
  useEffect(() => {
    const rs = location.state;
    if (rs?.restore) {
      setStep(2);
      setMode(rs.mode);
      if (rs.mode === 'strings') setStrings(rs.entries || [emptyString()]);
      else setFields(rs.entries || [emptyField()]);
      setResult(rs.result);
      window.history.replaceState({}, '');
    } else if (rs?.prefill) {
      setMode(rs.mode || 'fields');
      // Frag-HANS-Override für die Primärempfehlung (z. B. Generator-Upgrade
      // HS 6 PV → HS 9 PV) — verhindert Desync zwischen Chat-Karte und
      // PV-Konfigurator-Ergebnis.
      if (rs.recommendedModel) setRecommendedModelOverride(rs.recommendedModel);
      if (rs.entries && rs.entries.length > 0) {
        if ((rs.mode || 'fields') === 'strings') setStrings(rs.entries);
        else setFields(rs.entries);
        setStep(1);
        if (rs.autoCalc || rs.skipToResult) {
          // Auto-run calculation after mount → springt nach Step 2 (Ergebnis)
          setTimeout(() => {
            const btn = document.querySelector('[data-testid="btn-pv-next"]');
            if (btn && !btn.disabled) btn.click();
          }, 250);
        }
      } else {
        setStep(0);
      }
      window.history.replaceState({}, '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const entries = mode === 'strings' ? strings : fields;
  const setEntries = mode === 'strings' ? setStrings : setFields;
  const emptyEntry = mode === 'strings' ? emptyString : emptyField;

  const updateEntry = useCallback((id, key, val) => setEntries(prev => prev.map(e => e.id === id ? { ...e, [key]: val } : e)), [setEntries]);
  const addEntry = () => { if (entries.length < PV_DEFAULTS.maxStringsInConfigurator) setEntries(prev => [...prev, emptyEntry()]); };
  const addEntryWithCopy = () => {
    if (entries.length < PV_DEFAULTS.maxStringsInConfigurator && entries.length > 0) {
      const last = entries[entries.length - 1];
      setEntries(prev => [...prev, { ...emptyEntry(), moduleType: last.moduleType, vocPerModule: last.vocPerModule, powerWpPerModule: last.powerWpPerModule, nominalCurrentPerModule: last.nominalCurrentPerModule, betaVocPercent: last.betaVocPercent, minDesignTempC: last.minDesignTempC, iscPerModule: last.iscPerModule, vmpPerModule: last.vmpPerModule }]);
    }
  };
  const removeEntry = (id) => { if (entries.length > 1) setEntries(prev => prev.filter(e => e.id !== id)); };

  const canNext = () => {
    if (step === 0) return !!mode;
    if (step === 1) return entries.every(e => e.moduleCount > 0 && e.vocPerModule > 0 && e.powerWpPerModule > 0 && e.nominalCurrentPerModule > 0 && e.orientation);
    return false;
  };
  const goNext = () => { if (step === 1) computeResult(); setStep(s => Math.min(s + 1, 2)); };
  const progressStep = step === 2 ? 3 : step;

  // PDF module datasheet upload → returns variants for user to choose
  const handleModuleUpload = async (e, idx) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setModuleVariants(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/module-datasheet`, formData, { timeout: 60000 });
      if (res.data.success && res.data.variants && res.data.variants.length > 0) {
        if (res.data.variants.length === 1) {
          applyVariantToEntry(idx, res.data.variants[0]);
        } else {
          setModuleVariants({ idx, variants: res.data.variants, fileName: res.data.fileName });
        }
      } else {
        alert(res.data.error || 'Daten konnten nicht extrahiert werden. Bitte Werte manuell eingeben.');
      }
    } catch (err) {
      alert('Fehler beim Hochladen. Bitte Werte manuell eingeben.');
    }
    finally { setUploading(false); }
  };

  const applyVariantToEntry = (idx, variant) => {
    const id = entries[idx].id;
    if (variant.moduleType) updateEntry(id, 'moduleType', variant.moduleType);
    if (variant.vocPerModule) updateEntry(id, 'vocPerModule', String(variant.vocPerModule));
    if (variant.powerWp) updateEntry(id, 'powerWpPerModule', String(variant.powerWp));
    if (variant.nominalCurrentImp) updateEntry(id, 'nominalCurrentPerModule', String(variant.nominalCurrentImp));
    if (variant.vmpPerModule) updateEntry(id, 'vmpPerModule', String(variant.vmpPerModule));
    if (variant.betaVocPercent) updateEntry(id, 'betaVocPercent', String(variant.betaVocPercent));
    if (variant.iscPerModule) updateEntry(id, 'iscPerModule', String(variant.iscPerModule));
    setModuleVariants(null);
  };

  const computeResult = () => {
    const prepared = entries.map((e, idx) => ({
      ...e, moduleCount: Number(e.moduleCount), vocPerModule: Number(e.vocPerModule),
      powerWpPerModule: Number(e.powerWpPerModule), nominalCurrentPerModule: Number(e.nominalCurrentPerModule),
      betaVocPercent: e.betaVocPercent ? Number(e.betaVocPercent) : PV_DEFAULTS.betaVocPercent,
      minDesignTempC: e.minDesignTempC ? Number(e.minDesignTempC) : PV_DEFAULTS.minDesignTempC,
      vmpPerModule: e.vmpPerModule ? Number(e.vmpPerModule) : null,
      fieldName: e.fieldName || e.stringName || `${mode==='strings'?'String':'Feld'} ${idx+1}`,
      stringName: e.stringName || e.fieldName || `${mode==='strings'?'String':'Feld'} ${idx+1}`,
      orientation: e.orientation || 'sued',
    }));
    const res = findBestPvSystemSolutions(prepared, mode, annualConsumption || null, recommendedModelOverride);
    // Set displayName on assigned strings
    if (res.solutions) {
      res.solutions.forEach(sol => {
        sol.assignment.assigned.forEach(s => { s.displayName = s.displayName || s.stringName; });
        sol.assignment.unassigned.forEach(s => { s.displayName = s.displayName || s.stringName; });
      });
    }
    setResult(res);
  };

  const goToLeadForm = () => navigate('/anfrage', { state: { module: 'pv', summary: { mode, entries, result } } });

  // Sort assigned strings for technical details: ascending by displayName
  const sortedAssigned = (assigned) => [...assigned].sort((a, b) => {
    const nameA = a.displayName || a.stringName || '';
    const nameB = b.displayName || b.stringName || '';
    return nameA.localeCompare(nameB, 'de', { numeric: true });
  });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <StepProgress steps={STEPS} current={progressStep} />
      <main className="flex-1 pb-28 sm:pb-12">
        <div className="ees-container max-w-2xl">
          <div key={step} className="ees-step-enter">

            {step === 0 && (
              <div data-testid="step-pv-mode">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#32373c] mb-2">PV- und String-Konfigurator</h2>
                <p className="text-sm text-gray-500 mb-6">Technische Vorauswahl für Hans HS PV und Hans HS PV duo Systeme.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <SelectionCard icon={<List className="w-7 h-7" />} title="Strings bekannt" description="Ich kenne die fertigen PV-Strings und deren Konfiguration" selected={mode==='strings'} onClick={() => setMode('strings')} testId="card-mode-strings" />
                  <SelectionCard icon={<LayoutGrid className="w-7 h-7" />} title="Nur Modulfelder bekannt" description="Ich kenne die Modulfelder / Dachflächen und deren Module" selected={mode==='fields'} onClick={() => setMode('fields')} testId="card-mode-fields" />
                </div>
                <button onClick={() => setShowHelp(!showHelp)} className="text-xs text-[#9ec043] flex items-center gap-1 mb-3"><HelpCircle className="w-3.5 h-3.5" /> {showHelp ? 'Hilfe ausblenden' : 'Hilfe anzeigen'}</button>
                {showHelp && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-xs text-gray-500 space-y-2 mb-4">
                    <p><strong className="text-[#32373c]">String:</strong> Eine Reihenschaltung von PV-Modulen an einem MPPT-Tracker.</p>
                    <p><strong className="text-[#32373c]">Modulfeld:</strong> Eine Fläche mit PV-Modulen (z. B. Dachseite). Der Konfigurator teilt in Strings auf.</p>
                    <p><strong className="text-[#32373c]">Voc:</strong> Leerlaufspannung pro Modul (Datenblatt).</p>
                    <p><strong className="text-[#32373c]">Modulleistung (Wp):</strong> Nennleistung pro Modul in Watt peak.</p>
                    <p><strong className="text-[#32373c]">Nennstrom (Imp):</strong> Strom am MPP (Datenblatt).</p>
                  </div>
                )}
                <div className="text-xs text-gray-400 flex items-start gap-1.5 bg-[#f5f6f7] rounded p-3 border border-gray-100"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#9ec043]" /> Es können bis zu {PV_DEFAULTS.maxStringsInConfigurator} Strings geprüft werden. Bei Sonderkonfigurationen wenden Sie sich an order@ees-energiespeicher.de.</div>
              </div>
            )}

            {step === 1 && (
              <div data-testid="step-pv-entry">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#32373c] mb-2">{mode==='strings' ? 'String-Daten' : 'Modulfeld-Daten'} eingeben</h2>
                <p className="text-sm text-gray-500 mb-4">Geben Sie die Daten ein oder laden Sie ein Modul-Datenblatt hoch.</p>

                {/* Optional: Annual consumption */}
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-4">
                  <Label className="text-gray-500 text-xs">Jahresstromverbrauch in kWh (optional)</Label>
                  <Input type="number" placeholder="z. B. 5000" value={annualConsumption} onChange={e => setAnnualConsumption(e.target.value)} className="mt-1 bg-white border-gray-300 w-48" data-testid="input-annual-consumption" />
                  <p className="text-xs text-gray-400 mt-1">Wird zur Ermittlung der idealen Speichergröße herangezogen.</p>
                </div>
                <div className="space-y-4">
                  {entries.map((entry, idx) => (
                    <div key={entry.id} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-[#32373c]">{mode==='strings'?'String':'Feld'} {idx+1}</span>
                        <div className="flex items-center gap-2">
                          {/* PDF upload for module data */}
                          <label className={`text-xs cursor-pointer flex items-center gap-1 hover:underline ${uploading ? 'text-gray-400 pointer-events-none' : 'text-[#9ec043]'}`} data-testid={`btn-upload-module-${idx}`}>
                            {uploading ? (
                              <><svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg> <span className="hidden sm:inline">Wird analysiert...</span></>
                            ) : (
                              <><Upload className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Datenblatt</span></>
                            )}
                            <input type="file" accept=".pdf" onChange={e => handleModuleUpload(e, idx)} className="hidden" disabled={uploading} />
                          </label>
                          {entries.length > 1 && <button onClick={() => removeEntry(entry.id)} className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div><Label className="text-gray-500 text-xs">Bezeichnung</Label><Input value={mode==='strings'?entry.stringName:entry.fieldName} onChange={e => updateEntry(entry.id, mode==='strings'?'stringName':'fieldName', e.target.value)} placeholder={mode==='strings'?'z. B. Süd-String':'z. B. Süddach'} className="mt-1 bg-white border-gray-300 text-sm" data-testid={`input-name-${idx}`} /></div>
                        <div><Label className="text-gray-500 text-xs">Ausrichtung *</Label><select value={entry.orientation || ''} onChange={e => updateEntry(entry.id, 'orientation', e.target.value)} className="mt-1 w-full h-9 rounded-md border border-gray-300 bg-white px-2 text-sm" data-testid={`select-orientation-${idx}`}><option value="">Bitte wählen</option>{orientationOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
                        <div><Label className="text-gray-500 text-xs">Modultyp</Label><Input value={entry.moduleType} onChange={e => updateEntry(entry.id, 'moduleType', e.target.value)} placeholder="z. B. JA Solar 420W" className="mt-1 bg-white border-gray-300 text-sm" data-testid={`input-module-type-${idx}`} /></div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div><Label className="text-gray-500 text-xs">Anzahl Module *</Label><Input type="number" min="1" value={entry.moduleCount} onChange={e => updateEntry(entry.id, 'moduleCount', e.target.value)} placeholder="z. B. 12" className="mt-1 bg-white border-gray-300 text-sm" data-testid={`input-module-count-${idx}`} /></div>
                        <div><Label className="text-gray-500 text-xs">Voc/Modul (V) *</Label><Input type="number" step="0.1" value={entry.vocPerModule} onChange={e => updateEntry(entry.id, 'vocPerModule', e.target.value)} placeholder="z. B. 37.5" className="mt-1 bg-white border-gray-300 text-sm" data-testid={`input-voc-${idx}`} /></div>
                        <div><Label className="text-gray-500 text-xs">Leistung/Modul (Wp) *</Label><Input type="number" step="1" value={entry.powerWpPerModule} onChange={e => updateEntry(entry.id, 'powerWpPerModule', e.target.value)} placeholder="z. B. 420" className="mt-1 bg-white border-gray-300 text-sm" data-testid={`input-power-${idx}`} /></div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div><Label className="text-gray-500 text-xs">Nennstrom Imp (A) *</Label><Input type="number" step="0.01" value={entry.nominalCurrentPerModule} onChange={e => updateEntry(entry.id, 'nominalCurrentPerModule', e.target.value)} placeholder="z. B. 11.2" className="mt-1 bg-white border-gray-300 text-sm" data-testid={`input-current-${idx}`} /></div>
                        <div><Label className="text-gray-500 text-xs">Vmp/Modul (V)</Label><Input type="number" step="0.1" value={entry.vmpPerModule} onChange={e => updateEntry(entry.id, 'vmpPerModule', e.target.value)} placeholder="optional" className="mt-1 bg-white border-gray-300 text-sm" data-testid={`input-vmp-${idx}`} /></div>
                        <div><Label className="text-gray-500 text-xs">TK Voc (%/K)</Label><Input type="number" step="0.01" value={entry.betaVocPercent} onChange={e => updateEntry(entry.id, 'betaVocPercent', e.target.value)} placeholder={String(PV_DEFAULTS.betaVocPercent)} className="mt-1 bg-white border-gray-300 text-sm" data-testid={`input-beta-${idx}`} /></div>
                      </div>
                      {entry.moduleCount > 0 && entry.vocPerModule > 0 && entry.powerWpPerModule > 0 && (
                        <div className="text-xs text-gray-400 bg-white rounded p-2 grid grid-cols-3 gap-2 border border-gray-100">
                          <span>Voc kalt: {(Number(entry.moduleCount)*calculateColdVoc(Number(entry.vocPerModule), entry.betaVocPercent?Number(entry.betaVocPercent):null, entry.minDesignTempC?Number(entry.minDesignTempC):null)).toFixed(1)} V</span>
                          <span>Vmp est.: {(Number(entry.moduleCount)*calculateEstimatedVmp(Number(entry.vocPerModule), entry.vmpPerModule?Number(entry.vmpPerModule):null)).toFixed(1)} V</span>
                          <span>Leistung: {calculateStringPower(Number(entry.moduleCount), Number(entry.powerWpPerModule)).toFixed(2)} kW</span>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Module variant selection dialog */}
                  {moduleVariants && (
                    <div className="rounded-lg border-2 border-[#9ec043] bg-[#f8faf2] p-4 space-y-3" data-testid="variant-selection">
                      <h4 className="text-sm font-semibold text-[#32373c]">Modultyp auswählen</h4>
                      <p className="text-xs text-gray-500">Im Datenblatt wurden {moduleVariants.variants.length} Varianten gefunden. Bitte wählen Sie den gewünschten Typ:</p>
                      <div className="space-y-2">
                        {moduleVariants.variants.map((v, vi) => (
                          <button
                            key={vi}
                            onClick={() => applyVariantToEntry(moduleVariants.idx, v)}
                            className="w-full text-left rounded-md border border-gray-200 bg-white p-3 hover:border-[#9ec043] hover:bg-[#f8faf2] transition-colors"
                            data-testid={`variant-option-${vi}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-[#32373c]">{v.moduleType}</span>
                              <span className="text-xs text-[#9ec043] font-semibold">{v.powerWp} Wp</span>
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              Voc: {v.vocPerModule} V | Vmp: {v.vmpPerModule || '–'} V | Imp: {v.nominalCurrentImp} A | TK Voc: {v.betaVocPercent} %/K
                            </div>
                          </button>
                        ))}
                      </div>
                      <button onClick={() => setModuleVariants(null)} className="text-xs text-gray-400 underline">Abbrechen</button>
                    </div>
                  )}

                  {entries.length < PV_DEFAULTS.maxStringsInConfigurator && (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={addEntry} className="text-gray-500"><Plus className="w-4 h-4 mr-1" /> {mode==='strings'?'String':'Feld'} hinzufügen</Button>
                      {entries.length > 0 && <Button variant="outline" size="sm" onClick={addEntryWithCopy} className="text-[#9ec043] border-[#9ec043]/30 hover:bg-[#f0f7e0]" data-testid="btn-add-entry-copy"><Copy className="w-4 h-4 mr-1" /> Moduldaten übernehmen</Button>}
                    </div>
                  )}
                  <div className="text-xs text-gray-400 flex items-start gap-1.5 bg-[#f5f6f7] rounded p-3 border border-gray-100"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#9ec043]" /> Die Stromprüfung basiert auf dem angegebenen Nennstrom. Für die finale Auslegung sind vollständige Moduldaten zu prüfen.</div>
                </div>
              </div>
            )}

            {step === 2 && result && (
              <div data-testid="step-pv-result" id="pv-result-content">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#32373c] mb-2">PV-Systemempfehlung</h2>
                <p className="text-sm text-gray-500 mb-4">Gesamt-PV-Leistung: <strong className="text-[#32373c]">{result.totalPvKw} kWp</strong></p>

                {result.solutions.length === 0 ? (
                  <div className="text-center py-8"><AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" /><p className="text-sm text-gray-600">Kein passendes System gefunden.</p>{result.warnings.map((w,i) => <p key={i} className="text-xs text-gray-400 mt-2">{w}</p>)}</div>
                ) : (
                  <div className="space-y-4">
                    {result.solutions.map((sol, idx) => (
                      <div key={idx} className={idx===0?'ees-result-card p-5 sm:p-6':'rounded-xl border border-gray-200 bg-white p-5 sm:p-6'}>
                        <div className={`ees-badge ${idx===0?'ees-badge-green':'ees-badge-amber'} mb-3`}>{idx===0?'Empfohlenes System':'Alternative'}</div>
                        <h3 className="text-xl font-bold text-[#32373c] mb-1">{sol.system.displayName || getDisplayName(sol.system.model)}</h3>
                        <p className="text-xs text-gray-500 mb-2">Empfohlene PV-Leistung: {sol.system.recommendedPvKw} kWp | Max. Einspeiseleistung: {sol.system.maxFeedInKw} kW</p>
                        {sol.reason && <p className="text-xs text-[#9ec043] italic mb-4">{sol.reason}</p>}

                        {/* Additional battery from consumption analysis */}
                        {sol.additionalBattery && (
                          <div className="text-xs text-amber-700 flex items-start gap-1.5 bg-amber-50 rounded p-3 mb-3 border border-amber-200">
                            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Empfohlene zusätzliche Batteriekapazität: {sol.additionalBattery.modules} x {BATTERY_MODULE_BRUTTO_KWH} kWh (brutto) — basierend auf Ihrem Jahresverbrauch
                          </div>
                        )}

                        {/* System technical specs (visible on screen AND in PDF) */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4 p-3 rounded-lg bg-gray-50 border border-gray-100 text-xs">
                          <div><span className="text-gray-400">Empf. PV-Leistung</span><br/><span className="font-semibold text-[#32373c]">{sol.system.recommendedPvKw} kWp</span></div>
                          <div><span className="text-gray-400">Max. Einspeisung</span><br/><span className="font-semibold text-[#32373c]">{sol.system.maxFeedInKw} kW</span></div>
                          <div><span className="text-gray-400">Nutzbare Kapazität</span><br/><span className="font-semibold text-[#32373c]">{sol.system.standardUsableKwh} kWh</span></div>
                          <div><span className="text-gray-400">Max. Entladeleistung</span><br/><span className="font-semibold text-[#32373c]">{sol.system.maxDischargeKw} kW</span></div>
                          <div><span className="text-gray-400">Netzersatzleistung</span><br/><span className="font-semibold text-[#32373c]">{sol.system.backupContinuousKva} kVA</span></div>
                          <div><span className="text-gray-400">Netzersatz Peak (30s)</span><br/><span className="font-semibold text-[#32373c]">{sol.system.backupPeakKva30s} kVA</span></div>
                        </div>

                        <div className="mb-4"><h4 className="text-sm font-semibold text-[#32373c] mb-2">Trackerbelegung</h4>
                          <div className="space-y-1.5">{sol.assignment.trackers.filter(t => t.assigned.length > 0).map(t => (
                            <div key={t.id} className="flex items-center gap-2 text-sm"><span className="text-[#9ec043] font-mono text-xs w-8">{t.id}</span><span className="text-gray-700">{t.assigned.map(s => s.displayName || s.stringName).join(' + ')}</span><span className="text-gray-400 text-xs ml-auto">{t.totalPowerKw.toFixed(2)} kW | {t.totalCurrentA.toFixed(1)} A</span></div>
                          ))}</div>
                          {sol.assignment.unassigned.length > 0 && <div className="mt-2 text-xs text-red-600 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {sol.assignment.unassigned.length} String(s) konnten keinem Tracker zugeordnet werden.</div>}
                        </div>

                        <Accordion type="single" collapsible><AccordionItem value="details" className="border-gray-200">
                          <AccordionTrigger className="text-xs text-gray-400 hover:no-underline">Technische Details anzeigen</AccordionTrigger>
                          <AccordionContent className="text-xs text-gray-500 space-y-1">
                            {sortedAssigned(sol.assignment.assigned).map((s, si) => (
                              <div key={si} className="flex justify-between border-b border-gray-100 py-1">
                                <span>{s.displayName} ({s.moduleCount} Module)</span>
                                <span>Voc kalt: {s.stringVocCold?.toFixed(1)}V | Vmp: {s.stringVmpEstimate?.toFixed(1)}V | {s.powerKw?.toFixed(2)} kW</span>
                              </div>
                            ))}
                            {sol.vmpEstimated && <p className="mt-2 text-amber-600">Hinweis: Die untere Trackergrenze wurde näherungsweise über den geschätzten Vmp-Wert bewertet.</p>}
                          </AccordionContent>
                        </AccordionItem></Accordion>
                      </div>
                    ))}

                    {result.warnings.length > 0 && <div className="space-y-2">{result.warnings.map((w,i) => (<div key={i} className="text-xs text-amber-700 flex items-start gap-1.5 bg-amber-50 rounded p-3 border border-amber-200"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {w}</div>))}</div>}

                    <div data-pdf-exclude="true">
                      <Accordion type="single" collapsible><AccordionItem value="hints" className="border-gray-200"><AccordionTrigger className="text-sm text-gray-500 hover:no-underline">Hinweise</AccordionTrigger><AccordionContent className="text-xs text-gray-400 space-y-2">
                        <p>Unverbindliche technische Erstbewertung auf Basis Ihrer Eingaben.</p>
                        <p>Für die finale Auslegung sind vollständige Moduldaten, Temperaturannahmen und die Projektumgebung zu prüfen.</p>
                        <p>Bei mehr als 6 Strings oder Sonderkonfigurationen wenden Sie sich an order@ees-energiespeicher.de.</p>
                      </AccordionContent></AccordionItem></Accordion>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3" data-pdf-exclude="true">
                      <Button onClick={goToLeadForm} className="bg-[#9ec043] hover:bg-[#8aad38] text-white px-8 py-3 flex-1" data-testid="btn-pv-request">Beratung anfragen</Button>
                      <Button variant="outline" onClick={() => exportPvPdf(result)} className="text-gray-500 border-gray-300" data-testid="btn-pv-pdf"><Download className="w-4 h-4 mr-1" /> Als PDF speichern</Button>
                    </div>
                    <div className="mt-3" data-pdf-exclude="true"><Button variant="outline" asChild className="text-gray-500 border-gray-300 w-full"><a href="https://www.ees-energiespeicher.de/" target="_blank" rel="noopener noreferrer">Zurück zur EES-Startseite <ExternalLink className="w-4 h-4 ml-1" /></a></Button></div>
                  </div>
                )}
              </div>
            )}
          </div>

          {step < 2 && (
            <div className="flex justify-between items-center mt-8 pt-4 border-t border-gray-100">
              <Button variant="ghost" onClick={() => step===0?navigate('/'):setStep(s=>s-1)} className="text-gray-500"><ChevronLeft className="w-4 h-4 mr-1" /> {step===0?'Startseite':'Zurück'}</Button>
              <Button onClick={goNext} disabled={!canNext()} className="bg-[#9ec043] hover:bg-[#8aad38] text-white disabled:opacity-30" data-testid="btn-pv-next">{step===1?'Systeme berechnen':'Weiter'} <ChevronRight className="w-4 h-4 ml-1" /></Button>
            </div>
          )}
          {step === 2 && <div className="mt-6 pt-4 border-t border-gray-100"><Button variant="ghost" onClick={() => setStep(1)} className="text-gray-500"><ChevronLeft className="w-4 h-4 mr-1" /> Eingaben ändern</Button></div>}
        </div>
      </main>
      <FragHansFloating
        context={{
          configurator: 'pv',
          step,
          stepLabel: STEPS[step]?.label || 'Unbekannt',
          data: {
            mode: mode || null,
            entriesCount: entries.length,
            firstEntry: entries[0] ? {
              moduleCount: entries[0].moduleCount || null,
              moduleType: entries[0].moduleType || null,
              orientation: entries[0].orientation || null,
              powerWpPerModule: entries[0].powerWpPerModule || null,
            } : null,
          },
        }}
        initialGreeting={HANS_PV_GREETINGS[step] || HANS_PV_GREETINGS[0]}
      />
    </div>
  );
}
